import { Box, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { blackColor } from "theme";
import BottomActionButtons from "components/BottomActionButtons/BottomActionButtons";

import {
  useSubmitQuestionsMutation,
  useUpdateApplicationDataMutation
} from "../../../../redux/modules/catalog";
import NoCarrier from "./components/NoCarrier";

const CheckEligibility = ({
  showNoCarrier,
  setShowNoCarrier,
  setCarriers,
  applicationId,
  productSelected,
  setScreen,
  setStartApplicationData,
  applicationData,
  setLoading,
  applicationAPIData,
  refetch,
  setOpenSnackbar,
  setSnackbarData,
  setRiskType,
  setApplicationId
}) => {
  const isApplicationCreated =
    applicationAPIData?.application?.applicationData?.isDisabled;

  const navigate = useNavigate();
  const [questionData, setQuestionData] = useState([]);
  const formIoReff = useRef(null); // Create a ref to store the form instance

  const formJson = {...applicationAPIData?.application?.applicationData?.generatedFormBackend};
  const dataJson = {...applicationAPIData?.application?.applicationData?.dataJson};

  const options = {readOnly: applicationAPIData?.application?.applicationData?.isDisabled};

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (formJson) {
      window.Formio.createForm(
        document.getElementById("fromiooo"),
        formJson,
        options
      ).then((formIoForm) => {
        formIoReff.current = formIoForm;
        const data = { ...dataJson };
        formIoForm.everyComponent((component) => {
          if (data[component.component.key]) {
            component.setValue(
              JSON.parse(JSON.stringify(data[component.component.key]))
            );
          }
        });
      });
    }
  }, [formJson]);



  const [updateApplication] = useUpdateApplicationDataMutation();
  const [submitQuestion] = useSubmitQuestionsMutation();

  const handleSubmit = () => {
    if (isApplicationCreated) {
      navigate(
        `/start-application?currentScreen=StartApplication&applicationId=${applicationId}`
      );
      return;
    }

    if (formIoReff.current) {
      try {
        // First, tell FormIO to show validations on all components
        formIoReff.current.showErrors();

        // Check if the form is valid
        const isValid = formIoReff.current.checkValidity();

        if (!isValid) {
          // Force validation messages to display on all components
          formIoReff.current.everyComponent((component) => {
            if (
              component.component.validate &&
              component.component.validate.required
            ) {
              // Check if the field is empty and show error
              if (!component.getValue()) {
                component.setCustomValidity(
                  `${component.component.label || "This field"} is required`
                );
              }
            }
          });

          // Show the snackbar for general notification
          setOpenSnackbar(true);
          setSnackbarData({
            message: "Please fill all required fields",
            severity: "error"
          });

          return; // Stop the submission process
        }
        // Proceed with submission if form is valid
        formIoReff.current
          .submit()
          .then((submission) => {
            setRiskType(submission.data.RISK_TYPE);
            const payload = {
              data: {
                dataJson: submission.data,
                formJson,
                applicationId
              }
            };
            submitQuestion(payload)
              .unwrap()
              .then((res) => {
                setLoading(false);
                if (res?.result?.eligibleCarriers?.length === 0) {
                  setShowNoCarrier(true);
                } else {
                  navigate(
                    `/start-application?currentScreen=StartApplication&applicationId=${applicationId}`
                  );
                  setStartApplicationData(res?.result);
                  const payload = {
                    data: {
                      applicationId,
                      data: {
                        ...applicationData,
                        isDisabled: false,
                        questions: questionData,
                        productSelected,
                        dataJson: submission.data
                      }
                    }
                  };
                  updateApplication(payload)
                    .then(() => {
                      setCarriers(res?.result);
                      refetch();
                      setQuestionData([]);
                    })
                    .catch((err) => {
                      setOpenSnackbar(true);
                      setSnackbarData({
                        message: err.data.message,
                        severity: "error"
                      });
                    });
                }
              })
              .catch((err) => {
                setOpenSnackbar(true);
                setSnackbarData({
                  message: err.data.message,
                  severity: "error"
                });
              });
          })
          .catch((error) => {
            // This will catch any errors from validation or from the submission process
            if (error?.data?.message) {
              setOpenSnackbar(true);
              setSnackbarData({
                message: error.data.message,
                severity: "error"
              });
            }
          });
      } catch (error) {
        // Catch any synchronous errors
        setOpenSnackbar(true);
        setSnackbarData({
          message: "An error occurred",
          severity: "error"
        });
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Skip if the event occurs within a dropdown, select, or other form control that uses Enter
      const activeElement = document.activeElement;
      const isFormControlElement =
        activeElement.tagName === "SELECT" ||
        activeElement.classList.contains("choices__input") ||
        activeElement.classList.contains("form-control") ||
        activeElement.closest(".choices") ||
        activeElement.closest(".formio-component-select");

      // Only proceed with submit if not in a form control that needs Enter key
      if (event.key === "Enter" && !isFormControlElement) {
        event.preventDefault();
        handleSubmit(); // Use the same handleSubmit function with validation
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <Box
      sx={{
        zIndex: 9,
        height: "100%",
        width: "100%",
        position: "relative"
      }}
    >
      {showNoCarrier ? (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)"
          }}
        >
          <NoCarrier
            setScreen={setScreen}
            setShowNoCarrier={setShowNoCarrier}
            setApplicationId={setApplicationId}
            applicationId={applicationId}
          />
        </Box>
      ) : (
        <Box className="w-full h-full mt-0">
          <Typography fontSize="18px" fontWeight={500} color={blackColor}>
            Check eligibility
            <span style={{ fontWeight: 300, color: blackColor }}>
              &nbsp; by answering below questions
            </span>
          </Typography>
          <Box
            sx={{
              display: "flex",
              height: "auto",
              flexDirection: "column",
              justifyContent: "space-between",
              alignContent: "space-between"
            }}
          >
            <Box
              mt="1.6rem"
              sx={{
                maxHeight: "60vh",
                overflowY: "scroll",
                overflowX: "hidden",
                border: "1px solid #EFF2F6",
                borderRadius: "8px"
              }}
            >
              <Box component="form">
                <div id="fromiooo" ref={formIoReff} />
              </Box>
            </Box>

            <BottomActionButtons
              handleBack={() => {
                navigate(
                  `/start-application?currentScreen=SelectCarrier&applicationId=${applicationId}`,
                  { replace: true }
                );
              }}
              // isDisabled={!allHaveAnswer && !isApplicationCreated}
              isDisabled={false}
              // handleSubmit={() => handleAddQuestion()}
              handleSubmit={() => handleSubmit()}
              backButtonTitle="Back"
              nextButtonTitle="Check Eligiblity"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CheckEligibility;
